/**
 * The window's one "Share message" sheet (Slack forward; core ShareMessageViewModel, logic/share), mounted by the
 * shell: message menus open it with `openShare(message)`. Pick one or more conversations (chips; tap a row again to
 * remove it), add one comment, Share: a copy goes to each.
 */
import type { Message } from "@bundle/contracts";
import { ShareMessageViewModel } from "@bundle/core";
import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import { Button, Chip, Icon, ListRow, Sheet, Stack, Text, TextField, Toast } from "../primitives/index.ts";
import { useT } from "../i18n/I18nProvider.tsx";
import { useOwned } from "../hooks/owned.ts";
import { useViewModel, useWorkspace } from "../hooks/index.ts";

let pending: Message | undefined;
let seq = 0;
const listeners = new Set<() => void>();

/** Opens the share sheet for this message. */
export function openShare(message: Message): void {
  pending = message;
  seq += 1;
  for (const l of listeners) l();
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
};

export function ShareHost() {
  const t = useT();
  const ws = useWorkspace();
  const create = useCallback(() => new ShareMessageViewModel(ws.vm), [ws.vm]);
  const vm = useOwned(`share:${ws.vm.wsId}`, create);
  const s = useViewModel(vm);
  const request = useSyncExternalStore(subscribe, () => seq);
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (request > 0 && pending !== undefined) vm.open(pending);
  }, [request, vm]);
  const open = s.message !== undefined;
  return (
    <>
      <Sheet open={open} onClose={() => vm.close()} title={t("share.title")} testID="share-sheet">
        {open ? (
          <Stack gap="3" padding="4">
            {s.picked.length === 0 ? null : (
              <Stack direction="horizontal" gap="1" wrap accessibilityLabel={t("share.to")} testID="share-picked">
                {s.picked.map((d) => (
                  <Chip
                    key={d.channelId}
                    label={d.isDm ? d.title : `#${d.title}`}
                    icon={d.isDm ? "message" : d.isPrivate ? "lock" : undefined}
                    onRemove={() => vm.toggle(d.channelId)}
                    testID={`share-picked-${d.channelId}`}
                  />
                ))}
              </Stack>
            )}
            <TextField value={s.query} onChange={(q) => vm.setQuery(q)} placeholder={t("share.search")} accessibilityLabel={t("share.search")} leadingIcon="search" autoFocus testID="share-search" />
            <Stack gap="0.5">
                {s.targets.length === 0 ? (
                  <Text color="text.secondary">{t("share.empty")}</Text>
                ) : (
                  s.targets.slice(0, 8).map((d) => (
                    <ListRow
                      key={d.channelId}
                      title={d.title}
                      leading={<Icon name={d.isDm ? "message" : d.isPrivate ? "lock" : "hash"} size="icon.sm" />}
                      current={s.selected.includes(d.channelId)}
                      trailing={s.selected.includes(d.channelId) ? <Icon name="check" size="icon.sm" color="accent.fg" /> : undefined}
                      onPress={() => vm.toggle(d.channelId)}
                      density="regular"
                      testID={`share-target-${d.channelId}`}
                    />
                  ))
                )}
            </Stack>
            <TextField value={s.comment} onChange={(c) => vm.setComment(c)} placeholder={t("share.comment")} accessibilityLabel={t("share.comment")} multiline testID="share-comment" />
            {s.failed ? (
              <Text variant="footnote" color="status.danger.fg">
                {t("share.failed")}
              </Text>
            ) : null}
            <Stack direction="horizontal" gap="2" justify="end">
              <Button variant="ghost" label={t("common.cancel")} onPress={() => vm.close()} testID="share-cancel" />
              <Button
                variant="primary"
                label={t("share.send")}
                disabled={!vm.canSend}
                loading={s.sending}
                onPress={() => void vm.send().then((m) => setDone(m !== undefined))}
                testID="share-send"
              />
            </Stack>
          </Stack>
        ) : null}
      </Sheet>
      <Toast open={done} onClose={() => setDone(false)} message={t("share.done")} closeLabel={t("common.close")} durationMs={3000} testID="share-done" />
    </>
  );
}
